import React, { useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import ReferenceField from './ReferenceField';

// Operators available per field type. Mirrors backend _FIELD_TYPE_OPS.
const TEXT_OPS = ['eq', 'neq', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty', 'in', 'not_in'];
const NUMERIC_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_empty', 'is_not_empty'];
const BOOLEAN_OPS = ['eq', 'neq', 'is_empty', 'is_not_empty'];
const DATE_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_empty', 'is_not_empty'];
const MULTI_OPS = ['contains', 'not_contains', 'is_empty', 'is_not_empty'];

const OPS_BY_TYPE = {
  text: TEXT_OPS,
  url: TEXT_OPS,
  email: TEXT_OPS,
  phone: TEXT_OPS,
  select: TEXT_OPS,
  user: TEXT_OPS,
  object_relationship: TEXT_OPS,
  number: NUMERIC_OPS,
  boolean: BOOLEAN_OPS,
  date: DATE_OPS,
  datetime: DATE_OPS,
  multi_select: MULTI_OPS,
};

const OP_LABELS = {
  eq: 'equals',
  neq: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  gt: 'is greater than',
  gte: 'is greater than or equal to',
  lt: 'is less than',
  lte: 'is less than or equal to',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  in: 'is one of',
  not_in: 'is not one of',
};

const OPS_WITHOUT_VALUE = new Set(['is_empty', 'is_not_empty']);

const TIMESTAMP_FIELDS = [
  { slug: 'created_at', name: 'Created At', field_type: 'datetime' },
  { slug: 'updated_at', name: 'Updated At', field_type: 'datetime' },
];

function makeRuleId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `r_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function defaultRule() {
  return { _id: makeRuleId(), field: '', op: 'eq', value: '' };
}

function getOpsForField(fieldType) {
  return OPS_BY_TYPE[fieldType] || TEXT_OPS;
}

function ValueInput({ rule, fieldType, fieldOptions, onChange, upstreamNodes }) {
  if (OPS_WITHOUT_VALUE.has(rule.op)) return null;

  // Multi-value operators: comma-separated list
  if (rule.op === 'in' || rule.op === 'not_in') {
    const stringValue = Array.isArray(rule.value) ? rule.value.join(', ') : (rule.value || '');
    return (
      <ReferenceField
        label=""
        value={stringValue}
        onChange={(v) => onChange(v.split(',').map((s) => s.trim()).filter(Boolean))}
        placeholder="value1, value2, value3"
        upstreamNodes={upstreamNodes}
      />
    );
  }

  // Boolean: true/false dropdown
  if (fieldType === 'boolean') {
    const v = rule.value === true || rule.value === 'true' ? 'true' : 'false';
    return (
      <select
        value={v}
        onChange={(e) => onChange(e.target.value === 'true')}
        className="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  }

  // Select / multi_select with known options
  if ((fieldType === 'select' || fieldType === 'multi_select') && fieldOptions?.length > 0) {
    return (
      <select
        value={rule.value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-2.5 py-1.5 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-300 bg-white"
      >
        <option value="">Select…</option>
        {fieldOptions.map((opt) => (
          <option key={opt.value || opt} value={opt.value || opt}>{opt.label || opt}</option>
        ))}
      </select>
    );
  }

  // Default: ReferenceField (text input with {{node.path}} support)
  return (
    <ReferenceField
      label=""
      value={rule.value ?? ''}
      onChange={onChange}
      placeholder={fieldType === 'number' ? '0' : fieldType === 'date' ? 'YYYY-MM-DD' : 'value'}
      upstreamNodes={upstreamNodes}
    />
  );
}

export default function RuleBuilder({ filter, onChange, fieldDefs = [], upstreamNodes = [] }) {
  const safeFilter = filter && typeof filter === 'object'
    ? { match: filter.match || 'all', rules: Array.isArray(filter.rules) ? filter.rules : [] }
    : { match: 'all', rules: [] };

  // Backfill stable ids on rules loaded from saved configs so React reconciliation is correct.
  useEffect(() => {
    if (safeFilter.rules.some((r) => r && !r._id)) {
      onChange({
        ...safeFilter,
        rules: safeFilter.rules.map((r) => (r && r._id ? r : { ...r, _id: makeRuleId() })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const update = (next) => onChange(next);

  const setMatch = (match) => update({ ...safeFilter, match });

  const updateRule = (index, partial) => {
    const rules = safeFilter.rules.map((r, i) => (i === index ? { ...r, ...partial } : r));
    update({ ...safeFilter, rules });
  };

  const removeRule = (index) => {
    const rules = safeFilter.rules.filter((_, i) => i !== index);
    update({ ...safeFilter, rules });
  };

  const addRule = () => {
    update({ ...safeFilter, rules: [...safeFilter.rules, defaultRule()] });
  };

  const allFields = [
    ...fieldDefs.map((f) => ({ slug: f.slug, name: f.name, field_type: f.field_type, options: f.options })),
    ...TIMESTAMP_FIELDS,
  ];

  const lookupField = (slug) => allFields.find((f) => f.slug === slug);

  const handleFieldChange = (index, slug) => {
    const def = lookupField(slug);
    const ops = def ? getOpsForField(def.field_type) : TEXT_OPS;
    const currentOp = safeFilter.rules[index]?.op;
    const op = ops.includes(currentOp) ? currentOp : ops[0];
    updateRule(index, { field: slug, op, value: '' });
  };

  return (
    <div className="space-y-2">
      {/* Header: Match all/any */}
      <div className="flex items-center gap-2 text-[12px] text-gray-600">
        <span>Match</span>
        <select
          value={safeFilter.match}
          onChange={(e) => setMatch(e.target.value)}
          className="px-2 py-1 text-[12px] border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-300"
        >
          <option value="all">all</option>
          <option value="any">any</option>
        </select>
        <span>of the following rules:</span>
      </div>

      {/* Rules */}
      {safeFilter.rules.length === 0 && (
        <div className="text-[11px] text-gray-400 italic px-2 py-2 border border-dashed border-gray-200 rounded-md">
          No rules — returns all records (up to limit).
        </div>
      )}

      {safeFilter.rules.map((rule, index) => {
        const def = lookupField(rule.field);
        const fieldType = def?.field_type || 'text';
        const ops = getOpsForField(fieldType);
        const fieldOptions = def?.options?.choices || def?.options?.options || [];

        return (
          <div key={rule._id || index} className="p-2 border border-gray-200 rounded-md bg-gray-50/50 space-y-1.5">
            <div className="flex items-center gap-1.5">
              {/* Field */}
              <select
                value={rule.field}
                onChange={(e) => handleFieldChange(index, e.target.value)}
                className="flex-1 min-w-0 px-2 py-1 text-[12px] border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-300"
              >
                <option value="">Pick a field…</option>
                {fieldDefs.length > 0 && (
                  <optgroup label="Fields">
                    {fieldDefs.map((f) => (
                      <option key={f.slug} value={f.slug}>{f.name}</option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Timestamps">
                  {TIMESTAMP_FIELDS.map((f) => (
                    <option key={f.slug} value={f.slug}>{f.name}</option>
                  ))}
                </optgroup>
              </select>

              {/* Operator */}
              <select
                value={rule.op}
                onChange={(e) => updateRule(index, { op: e.target.value, value: OPS_WITHOUT_VALUE.has(e.target.value) ? null : (rule.value ?? '') })}
                disabled={!rule.field}
                className="flex-1 min-w-0 px-2 py-1 text-[12px] border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-300 disabled:bg-gray-100 disabled:text-gray-400"
              >
                {ops.map((op) => (
                  <option key={op} value={op}>{OP_LABELS[op] || op}</option>
                ))}
              </select>

              {/* Delete */}
              <button
                type="button"
                onClick={() => removeRule(index)}
                className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                title="Remove rule"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Value */}
            {!OPS_WITHOUT_VALUE.has(rule.op) && rule.field && (
              <ValueInput
                rule={rule}
                fieldType={fieldType}
                fieldOptions={fieldOptions}
                onChange={(v) => updateRule(index, { value: v })}
                upstreamNodes={upstreamNodes}
              />
            )}
          </div>
        );
      })}

      {/* Add rule */}
      <button
        type="button"
        onClick={addRule}
        className="flex items-center gap-1 px-2 py-1 text-[12px] text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        Add rule
      </button>
    </div>
  );
}
